import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const UserRoleIndicator = ({ userRole = 'contractor', userName = 'Jan de Vries' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const roleConfig = {
    superadmin: {
      label: 'Super Administrator',
      icon: 'Shield',
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    client: {
      label: 'Opdrachtgever',
      icon: 'Building2',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    contractor: {
      label: 'Opdrachtnemer',
      icon: 'Wrench',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    affiliate: {
      label: 'Partner',
      icon: 'Users',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  };

  const currentRole = roleConfig?.[userRole] || roleConfig?.contractor;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        onClick={toggleDropdown}
        className="flex items-center gap-3 px-3 py-2 h-auto"
      >
        <div className={`w-8 h-8 ${currentRole?.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon name={currentRole?.icon} size={18} color={`var(--color-${userRole === 'superadmin' ? 'error' : userRole === 'client' ? 'primary' : userRole === 'contractor' ? 'accent' : 'success'})`} />
        </div>
        <div className="hidden md:flex flex-col items-start">
          <span className="font-medium text-sm text-foreground leading-tight">
            {userName}
          </span>
          <span className={`caption ${currentRole?.color} leading-tight`}>
            {currentRole?.label}
          </span>
        </div>
        <Icon 
          name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
          size={16} 
          className="text-muted-foreground hidden md:block"
        />
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 z-[1010] overflow-hidden" style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #1A1A1A' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,229,160,0.1)' }}>
                <Icon name={currentRole?.icon} size={20} color="#00E5A0" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body font-semibold text-sm text-white truncate">{userName}</p>
                <p className="text-xs truncate" style={{ color: '#00E5A0' }}>{currentRole?.label}</p>
              </div>
            </div>
          </div>

          <div className="py-2">
            <Link to="/user-profile-management" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm font-body transition-colors hover:text-white" style={{ color: '#888888' }}>
              <Icon name="User" size={16} color="#555555" />
              <span>Profiel beheren</span>
            </Link>
            <Link to="/settings" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm font-body transition-colors hover:text-white" style={{ color: '#888888' }}>
              <Icon name="Settings" size={16} color="#555555" />
              <span>Instellingen</span>
            </Link>
            {userRole === 'superadmin' && (
              <Link to="/admin" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm font-body transition-colors hover:text-white" style={{ color: '#888888' }}>
                <Icon name="Shield" size={16} color="#555555" />
                <span>Beheer paneel</span>
              </Link>
            )}
          </div>

          <div className="py-2" style={{ borderTop: '1px solid #1A1A1A' }}>
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2 text-sm font-body w-full transition-colors" style={{ color: '#EF4444' }}>
              <Icon name="LogOut" size={16} color="#EF4444" />
              <span>Uitloggen</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoleIndicator;